import Page from '@service-victoria/component-library/components/Page';
import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import { app as Context } from 'app/context';
import { userManager } from 'app/services';
import { fetchProviders, getProviderDetail } from 'app/services/gakService';
import { constants, logger } from 'app/utils';

import AccountError from './AccountError';
import NotLinkedToProviderError from './NotLinkedToProviderError';

export type ComponentProps = unknown;

const AccountCallback: React.FC<ComponentProps> = () => {
  const { actions } = Context.useAppContext();
  const [status, setStatus] = useState({
    loading: true,
    error: undefined as Error | undefined
  });
  const history = useHistory();

  const continueToDashboard = useCallback(async () => {
    try {
      // Get list of linked activity providers
      const availableProviders = await fetchProviders();

      if (availableProviders.length > 0) {
        // Fetch first available activity provider's details
        const providerDetails = await getProviderDetail(availableProviders[0].providerId);

        // Persist provider list and detail
        actions.setAvailableProviders(availableProviders);
        actions.setProviderDetails(providerDetails);

        // Route to the dashboard
        history.push(`${constants.Routes.Dashboard}`);
      } else {
        // We don't have a not registerd step just yet, Redirect to Error at this stage.
        throw new Error('User not linked to registered provider');
      }
    } catch (error: any) {
      logger.error('Error when checking if user is linked to provider', error);
      setStatus({ loading: false, error });
    }
  }, [history, actions]);

  const renderError = error => {
    switch (error.message) {
      case 'User not linked to registered provider':
        return <NotLinkedToProviderError />;
      default:
        return <AccountError />;
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        await userManager.signinUser();
        const user = await userManager.getUserData();

        if (user && user.id_token) {
          // Set the ID token
          actions.setIdToken(user.id_token);

          // Check if user is linked to a valid provider then open dashboard
          continueToDashboard();
        } else {
          throw new Error('ID token not issued to user');
        }
      } catch (error: any) {
        setStatus({ loading: false, error });

        // State found, indicating that the auth code was not already exchanged, and a user has not navigated
        // here directly without logging in first.
        if (error.message !== 'No matching state found in storage') {
          logger.error('Error during IDAM callback', error);
        }
      }
    };
    load();
  }, [actions, continueToDashboard, history]);

  return (
    <Page
      loading={status.loading}
      title={constants.RedemptionPage.title}
      headerProps={constants.headerProps}
      showFooter>
      {status.error && renderError(status.error)}
    </Page>
  );
};

export default AccountCallback;
